import { useMemo, useState } from 'react'

import { getCurrency } from '@common/utils/currency'
import { type ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'

import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import FolderIcon from '@/assets/icons/icon-folder.svg'
import { Button } from '@/components/ui/button.tsx'
import { getFormattedDateRange } from '@/components/ui/export.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { DataTable } from '@/components/ui/table/data-table.tsx'
import { formatNumbersForSheet, getPercentValue } from '@/utils/helpers'

import { usePpvTracking } from '../hooks/usePpvTracking.tsx'
import type { PpvTrackingCategory } from '../types'
import { PpvTrackingQueryPanel } from './PpvTrackingQueryPanel.tsx'

interface PpvTrackingTableProps {
  showCents?: boolean
}

const tableColumnTitles = {
  name: 'Name',
  revenue: 'Revenue',
  purchaseRate: 'Purchase Rate',
  boughtAmount: 'Purchases',
}

export const PpvOverviewTable = ({ showCents }: PpvTrackingTableProps) => {
  const redirect = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')

  const handleButtonClick = (index: number) => () => {
    redirect(`${index}`)
  }

  const { allCategories, isFetching, date } = usePpvTracking()

  const filteredCategories = useMemo(() => {
    if (!allCategories) return []

    return allCategories.filter((category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }, [allCategories, searchQuery])

  const columns: ColumnDef<PpvTrackingCategory>[] = [
    {
      header: tableColumnTitles.name,
      accessorKey: 'name',
      enableSorting: true,
      sortingFn: 'text',
      cell: ({ row }) => (
        <div
          className="flex cursor-pointer items-center gap-2.5"
          onClick={handleButtonClick(row.index)}
        >
          <FolderIcon className="min-w-[16px] fill-secondary-500" />
          <div className="... w-[180px] truncate xl:w-[250px]">
            {row.original.name === 'uncategorized'
              ? 'Uncategorized'
              : row.original.name}
          </div>
        </div>
      ),
    },
    {
      header: tableColumnTitles.revenue,
      accessorKey: 'netRevenue',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: ({ getValue }) => (
        <div>{getCurrency(getValue() as string, showCents ? 2 : 0)}</div>
      ),
    },
    {
      header: tableColumnTitles.purchaseRate,
      accessorKey: 'purchaseRate',
      enableSorting: true,
      sortingFn: (a, b) => {
        const aPercent = a.original.purchaseRate
        const bPercent = b.original.purchaseRate

        return aPercent - bPercent
      },
      cell: ({ getValue }) => (
        <div>{getPercentValue(getValue() as number, showCents ? 2 : 0)}</div>
      ),
    },
    {
      header: tableColumnTitles.boughtAmount,
      accessorKey: 'boughtAmount',
      enableSorting: true,
      sortingFn: 'alphanumeric',
    },
    {
      header: () => <span className="w-full text-right">Action</span>,
      id: 'action',
      cell: ({ row }) => (
        <div className="float-right">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleButtonClick(row.index)}
            className="whitespace-nowrap p-0"
          >
            Open Folder
            <RightChevronIcon className="fill-primary-500" />
          </Button>
        </div>
      ),
    },
  ]

  if (isFetching || !allCategories) {
    return <Skeleton className="h-80 w-full" />
  }

  const getPPVTrackingTableSheetData = () => {
    return filteredCategories.map((category) => ({
      [tableColumnTitles.name]:
        category.name === 'uncategorized' ? 'Uncategorized' : category.name,
      [tableColumnTitles.revenue]: formatNumbersForSheet(category.netRevenue),
      [tableColumnTitles.purchaseRate]: category.purchaseRate,
      [tableColumnTitles.boughtAmount]: formatNumbersForSheet(
        category.boughtAmount,
      ),
    }))
  }

  return (
    <DataTable
      enablePagination
      enableSorting
      enableExport
      title="PPV Tracking"
      titleClassName="text-3xl"
      extra={<PpvTrackingQueryPanel setSearch={setSearchQuery} />}
      exportOptions={{
        fileName: `PPV Tracking, ${getFormattedDateRange(date)}`,
        getTableSheetData: getPPVTrackingTableSheetData,
      }}
      columns={columns}
      data={filteredCategories}
      className="px-6 py-0"
      wrapperClassName="gap-2"
    />
  )
}
